import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyUserLanguage } from '../../i18n';
import './onboarding-wizard.scss';
import {
  completeWizard,
  fetchWizardState,
  FirstTaskSuggestion,
  WizardPayload,
} from './wizardApi';
import { taskLabel } from './TasksStep';
import NoAccountModal from './NoAccountModal';
import OnboardingFlow from './OnboardingFlow';

const listWithAnd = (items: string[], and: string): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;

const OnboardingWizardPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // The sample Morning List swaps the URL token for its own while it's open.
  const [token] = useState(() => searchParams.get('token'));

  const [payload, setPayload] = useState<WizardPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [carryOn, setCarryOn] = useState<(() => void) | null>(null);
  const [oauthNotice, setOauthNotice] = useState<string | null>(null);
  const [oauthNoticeTitle, setOauthNoticeTitle] = useState<string | null>(null);
  const dismissOauthNotice = useCallback(() => {
    setOauthNotice(null);
    setOauthNoticeTitle(null);
  }, []);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3200);
  }, []);

  useEffect(() => {
    const status = searchParams.get('status');
    if (!status) {
      return;
    }
    const noMailbox = searchParams.get('noMailbox') === '1';
    if (status === 'success' && (searchParams.get('missing') || noMailbox)) {
      // A Google account without a Gmail service connects fine but can never
      // do email; that is a different message from "you didn't tick Email".
      const missingLabels = (searchParams.get('missing') || '')
        .split(',')
        .filter(Boolean)
        .filter((label) => !(noMailbox && label === 'Email'));
      const scopeNotice =
        missingLabels.length > 0
          ? t('wizard.oauthMissingScopes', {
              scopes: listWithAnd(missingLabels, t('wizard.listAnd')),
              area:
                missingLabels.length > 1
                  ? t('wizard.oauthMissingAreaMulti')
                  : t('wizard.oauthMissingAreaSingle'),
            })
          : '';
      if (noMailbox) {
        setOauthNoticeTitle(t('wizard.oauthNoMailboxTitle'));
        setOauthNotice(
          [t('wizard.oauthNoMailbox'), scopeNotice].filter(Boolean).join(' '),
        );
      } else {
        setOauthNotice(scopeNotice);
      }
    } else if (status === 'already-linked-other') {
      setOauthNotice(t('wizard.oauthAlreadyLinkedOther'));
    } else if (status === 'expired') {
      setOauthNotice(t('wizard.oauthExpired'));
    } else if (status === 'invalid-token') {
      setOauthNotice(t('wizard.oauthInvalidToken'));
    } else if (status === 'error') {
      setOauthNotice(t('wizard.oauthError'));
    }
    setSearchParams(
      (params) => {
        params.delete('status');
        params.delete('missing');
        params.delete('noMailbox');
        return params;
      },
      { replace: true },
    );
    // Runs once on mount: the status param comes from the OAuth redirect.
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!token) {
      setLoadError(t('wizard.missingToken'));
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchWizardState(token)
      .then((data) => {
        if (cancelled) {
          return;
        }
        setPayload(data);
        applyUserLanguage(data.language);
        setCompleted(Boolean(data.state.completedAt));
        setLoading(false);
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setLoadError(
          error instanceof Error ? error.message : t('wizard.loadFailed'),
        );
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const refresh = useCallback(() => {
    if (!token) {
      return;
    }
    fetchWizardState(token)
      .then(setPayload)
      .catch(() => undefined);
  }, [token]);

  // First-task research and memory highlights fill in while the user carries
  // on through the walkthrough.
  const accountCount = payload?.accounts.length ?? 0;
  const firstTasksStatus = payload?.state.firstTasks?.status;
  const memoriesStatus = payload?.state.memoryHighlights?.status;
  const researchRunning =
    accountCount > 0 &&
    (!firstTasksStatus ||
      firstTasksStatus === 'running' ||
      !memoriesStatus ||
      memoriesStatus === 'running');
  useEffect(() => {
    if (!token || !researchRunning || completed) {
      return;
    }
    const interval = window.setInterval(refresh, 5000);
    return () => window.clearInterval(interval);
  }, [token, researchRunning, completed, refresh]);

  const handleComplete = async (
    firstTask: string,
    suggestions: FirstTaskSuggestion[],
  ) => {
    if (!token || !payload) {
      return;
    }
    try {
      const updated = await completeWizard(token, firstTask);
      setPayload(updated);
      showToast(
        t('wizard.openingWhatsAppToast', {
          task: taskLabel(firstTask, suggestions, t),
          name: updated.assistantName,
        }),
      );
      await new Promise((resolve) => window.setTimeout(resolve, 1200));
      if (updated.returnToWhatsAppUrl) {
        window.location.assign(updated.returnToWhatsAppUrl);
      } else {
        setCompleted(true);
      }
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : t('wizard.genericError'),
      );
    }
  };

  if (loading) {
    return (
      <div className='ob-page'>
        <div className='ob-fallback' aria-label={t('wizard.loadingSetup')}>
          <span className='done-spinner' />
        </div>
      </div>
    );
  }

  if (loadError || !token || !payload) {
    return (
      <div className='ob-page'>
        <div className='ob-fallback'>
          <h1>{t('wizard.linkErrorTitle')}</h1>
          <p>{loadError ?? t('wizard.loadFailed')}</p>
        </div>
      </div>
    );
  }

  if (completed) {
    return (
      <div className='ob-page'>
        <div className='ob-fallback'>
          <h1>{t('wizard.allSetTitle')}</h1>
          <p>{t('wizard.allSetBody', { name: payload.assistantName })}</p>
          {payload.returnToWhatsAppUrl && (
            <a className='btn-primary' href={payload.returnToWhatsAppUrl}>
              {t('wizard.openWhatsApp')}
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <OnboardingFlow
        live={{
          token,
          payload,
          setPayload,
          refresh,
          oauthNotice,
          oauthNoticeTitle,
          onDismissNotice: dismissOauthNotice,
          onError: showToast,
          onNoAccount: (next) => setCarryOn(() => next),
          onComplete: handleComplete,
        }}
      />
      {carryOn && (
        <NoAccountModal
          assistantName={payload.assistantName}
          onConnectInstead={() => setCarryOn(null)}
          onCarryOn={() => {
            setCarryOn(null);
            carryOn();
          }}
        />
      )}
      <div className={`toast${toast ? ' show' : ''}`}>{toast}</div>
    </>
  );
};

export default OnboardingWizardPage;
